/* ==========================================================================
   PROTOCOL CASE — v3 theme scripts
   ========================================================================== */
(() => {
  'use strict';

  const settings = window.themeSettings || {};
  const moneyFormat = settings.moneyFormat || '${{amount}}';

  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  };

  /* ------------------------------------------------------------------
     Header: measure height into CSS var, add scrolled state
     ------------------------------------------------------------------ */
  const header = document.querySelector('[data-site-header]');

  const setHeaderHeight = () => {
    if (!header) return;
    document.documentElement.style.setProperty('--site-header-height', `${header.offsetHeight}px`);
  };
  setHeaderHeight();
  window.addEventListener('resize', setHeaderHeight);
  window.addEventListener('load', setHeaderHeight);

  let lastScroll = 0;
  window.addEventListener('scroll', () => {
    if (!header) return;
    const y = window.scrollY;
    header.classList.toggle('is-scrolled', y > 8);
    lastScroll = y;
  }, { passive: true });

  /* ------------------------------------------------------------------
     Drawers (nav + cart) — shared open/close with focus + scroll lock
     ------------------------------------------------------------------ */
  let scrollLocked = 0;
  const lockScroll = () => { scrollLocked++; document.body.style.overflow = 'hidden'; };
  const unlockScroll = () => { scrollLocked = Math.max(0, scrollLocked - 1); if (!scrollLocked) document.body.style.overflow = ''; };

  const setupDrawer = (root) => {
    if (!root) return null;
    const api = {
      root,
      open() {
        root.classList.add('is-open');
        root.removeAttribute('aria-hidden');
        lockScroll();
        const focusable = root.querySelector('button, a, input');
        if (focusable) focusable.focus({ preventScroll: true });
      },
      close() {
        if (!root.classList.contains('is-open')) return;
        root.classList.remove('is-open');
        root.setAttribute('aria-hidden', 'true');
        unlockScroll();
      },
      toggle() { root.classList.contains('is-open') ? this.close() : this.open(); },
    };
    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]') || e.target.classList.contains('nav-drawer__overlay') || e.target.classList.contains('cart-drawer__overlay')) {
        api.close();
      }
    });
    return api;
  };

  const navDrawer = setupDrawer(document.querySelector('[data-nav-drawer]'));
  let cartDrawer = setupDrawer(document.querySelector('[data-cart-drawer]'));

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-nav-open]')) { e.preventDefault(); navDrawer && navDrawer.open(); }
    if (e.target.closest('[data-cart-open]')) { e.preventDefault(); cartDrawer && cartDrawer.open(); }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { navDrawer && navDrawer.close(); cartDrawer && cartDrawer.close(); }
  });

  /* ------------------------------------------------------------------
     Cart engine — AJAX add / change + drawer re-render
     ------------------------------------------------------------------ */
  const updateCartCounts = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
      el.classList.remove('is-bumped');
      void el.offsetWidth; // restart animation
      el.classList.add('is-bumped');
    });
  };

  const rerenderCartDrawer = async () => {
    try {
      const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      const data = await res.json();
      const html = data['cart-drawer'];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-cart-drawer]');
      const current = document.querySelector('[data-cart-drawer]');
      if (fresh && current) {
        const wasOpen = current.classList.contains('is-open');
        current.innerHTML = fresh.innerHTML;
        if (wasOpen) current.classList.add('is-open');
        const countEl = fresh.querySelector('[data-drawer-item-count]');
        if (countEl) updateCartCounts(parseInt(countEl.dataset.drawerItemCount, 10) || 0);
      }
    } catch (err) { /* network hiccup: leave drawer as-is */ }
  };

  const addToCart = async (body) => {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  };

  const changeCart = async (lineKey, quantity) => {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity }),
    });
  };

  // Intercept product forms marked data-ajax-add
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-ajax-add]');
    if (!form) return;
    e.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const buttonText = button && button.querySelector('[data-add-button-text]');
    const originalLabel = buttonText ? buttonText.textContent : null;
    if (button) { button.disabled = true; button.classList.add('is-loading'); }
    if (buttonText) buttonText.textContent = button.dataset.addingLabel || 'Adding…';

    try {
      const fd = new FormData(form);
      const payload = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity') || '1', 10) };
      await addToCart(payload);
      await rerenderCartDrawer();
      if (buttonText) buttonText.textContent = button.dataset.addedLabel || 'Added ✓';
      cartDrawer && cartDrawer.open();
      setTimeout(() => {
        if (buttonText && originalLabel) buttonText.textContent = originalLabel;
        if (button) { button.disabled = false; button.classList.remove('is-loading'); }
      }, 900);
    } catch (err) {
      if (buttonText) buttonText.textContent = err.message.slice(0, 60);
      setTimeout(() => {
        if (buttonText && originalLabel) buttonText.textContent = originalLabel;
        if (button) { button.disabled = false; button.classList.remove('is-loading'); }
      }, 2200);
    }
  });

  // Drawer interactions: qty steppers, remove, upsell add (delegated — drawer re-renders)
  document.addEventListener('click', async (e) => {
    const stepBtn = e.target.closest('[data-line-step]');
    if (stepBtn) {
      const line = stepBtn.closest('[data-line-key]');
      if (!line) return;
      const input = line.querySelector('[data-line-qty]');
      const current = parseInt(input.value, 10) || 0;
      const next = Math.max(0, current + parseInt(stepBtn.dataset.lineStep, 10));
      stepBtn.disabled = true;
      await changeCart(line.dataset.lineKey, next);
      await rerenderCartDrawer();
      return;
    }

    const removeBtn = e.target.closest('[data-line-remove]');
    if (removeBtn) {
      const line = removeBtn.closest('[data-line-key]');
      if (!line) return;
      removeBtn.disabled = true;
      await changeCart(line.dataset.lineKey, 0);
      await rerenderCartDrawer();
      return;
    }

    const upsellBtn = e.target.closest('[data-upsell-add]');
    if (upsellBtn) {
      upsellBtn.disabled = true;
      const label = upsellBtn.textContent;
      upsellBtn.textContent = '…';
      try {
        await addToCart({ id: parseInt(upsellBtn.dataset.upsellAdd, 10), quantity: 1 });
        await rerenderCartDrawer();
      } catch (err) {
        upsellBtn.textContent = label;
        upsellBtn.disabled = false;
      }
      return;
    }

    // Generic quantity steppers (PDP)
    const qtyBtn = e.target.closest('[data-qty-step]');
    if (qtyBtn) {
      const wrap = qtyBtn.closest('.quantity');
      const input = wrap && wrap.querySelector('input');
      if (!input) return;
      const next = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(qtyBtn.dataset.qtyStep, 10));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* ------------------------------------------------------------------
     Product section: variant selection, media, price, sticky bar
     ------------------------------------------------------------------ */
  class ProductSection {
    constructor(section) {
      this.section = section;
      const jsonEl = section.querySelector('[data-product-json]');
      if (!jsonEl) return;
      this.product = JSON.parse(jsonEl.textContent);
      const mediaMapEl = section.querySelector('[data-variant-media-json]');
      this.variantMedia = mediaMapEl ? JSON.parse(mediaMapEl.textContent) : {};

      this.mainImage = section.querySelector('[data-main-product-image]');
      this.thumbs = Array.from(section.querySelectorAll('[data-media-thumbnail]'));
      this.priceEls = section.querySelectorAll('[data-price]');
      this.stickyPrice = section.querySelector('[data-sticky-price]');
      this.stickyVariantLabel = section.querySelector('[data-sticky-variant-label]');
      this.variantInputs = section.querySelectorAll('[data-variant-id]');
      this.addButtons = section.querySelectorAll('[data-add-button]');

      section.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-input]')) this.onOptionChange();
      });

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.showMedia(t)));
      this.setupStickyBar();
    }

    selectedOptions() {
      return Array.from(this.section.querySelectorAll('[data-option-input]:checked'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((i) => i.value);
    }

    findVariant() {
      const sel = this.selectedOptions();
      return this.product.variants.find((v) => {
        const opts = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter(Boolean);
        return sel.every((o, i) => opts[i] === o);
      });
    }

    onOptionChange() {
      const sel = this.selectedOptions();
      this.section.querySelectorAll('[data-selected-option]').forEach((label) => {
        const idx = Number(label.dataset.selectedOption) - 1;
        label.textContent = sel[idx] || '';
      });

      const variant = this.findVariant();
      if (!variant) { this.setState(false, null); return; }

      this.variantInputs.forEach((input) => { input.value = variant.id; });
      this.priceEls.forEach((el) => { el.textContent = formatMoney(variant.price); });
      if (this.stickyPrice) this.stickyPrice.textContent = formatMoney(variant.price);
      if (this.stickyVariantLabel) this.stickyVariantLabel.textContent = variant.title !== 'Default Title' ? variant.title : '';
      this.setState(variant.available, variant);

      const mediaId = this.variantMedia[String(variant.id)];
      if (mediaId) {
        const thumb = this.thumbs.find((t) => t.dataset.mediaId === String(mediaId));
        if (thumb) this.showMedia(thumb);
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    setState(available, variant) {
      this.addButtons.forEach((btn) => {
        const textEl = btn.querySelector('[data-add-button-text]');
        btn.disabled = !available;
        if (textEl) {
          textEl.textContent = available
            ? (btn.dataset.addLabel || 'Add to cart')
            : (variant ? (btn.dataset.soldOutLabel || 'Sold out') : (btn.dataset.unavailableLabel || 'Unavailable'));
        }
      });
    }

    showMedia(thumb) {
      if (!this.mainImage || !thumb.dataset.mediaSrc) return;
      const img = this.mainImage;
      img.classList.add('is-fading');
      const swap = () => {
        img.src = thumb.dataset.mediaSrc;
        if (thumb.dataset.mediaSrcset) img.srcset = thumb.dataset.mediaSrcset; else img.removeAttribute('srcset');
        img.alt = thumb.dataset.mediaAlt || img.alt;
        img.onload = () => img.classList.remove('is-fading');
        setTimeout(() => img.classList.remove('is-fading'), 400);
      };
      setTimeout(swap, 120);
      this.thumbs.forEach((t) => {
        const active = t === thumb;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      thumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    }

    setupStickyBar() {
      const sticky = this.section.querySelector('[data-product-sticky-cta]');
      const anchor = this.section.querySelector('[data-buy-anchor]');
      if (!sticky || !anchor || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(([entry]) => {
        const below = entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', !entry.isIntersecting && below);
      }, { rootMargin: '-60px 0px 0px 0px' });
      io.observe(anchor);
    }
  }

  document.querySelectorAll('[data-product-section]').forEach((s) => new ProductSection(s));

  /* ------------------------------------------------------------------
     Scroll reveals
     ------------------------------------------------------------------ */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const revealEls = document.querySelectorAll('[data-reveal]');
  if (reduceMotion || !('IntersectionObserver' in window)) {
    revealEls.forEach((el) => el.classList.add('is-visible'));
  } else {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    revealEls.forEach((el) => io.observe(el));
  }

  /* ------------------------------------------------------------------
     Collection sort
     ------------------------------------------------------------------ */
  document.querySelectorAll('[data-sort-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.href = url.toString();
    });
  });
})();
